import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FIX 3 (onboarding wizard) — dashboard reminder for properties whose
 * landlord_id is null. Triggered when an agent skips the
 * "Create new landlord profile?" prompt on Step 4 of the wizard.
 *
 * Follow-up FIX 4A — each row now has an inline "Link Landlord" action
 * that opens a dropdown of the agency's existing landlord profiles; the
 * agent can link without leaving the dashboard.
 */
export default function PropertiesWithoutLandlordBanner({ onGoToSettings }) {
  const [items, setItems] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [openFor, setOpenFor] = useState(null);      // property_id with open dropdown
  const [linking, setLinking] = useState(null);      // property_id being linked right now

  // FIX 2 (body-stream) — always read as text + JSON.parse.
  const safeJsonFetch = async (url, init) => {
    const res = await fetch(url, { credentials: 'include', ...(init || {}) });
    const text = await res.text();
    let data = {};
    if (text) { try { data = JSON.parse(text); } catch { data = { detail: text }; } }
    return { ok: res.ok, status: res.status, data };
  };

  const loadProperties = async () => {
    const { ok, data } = await safeJsonFetch(
      `${API_BASE_URL}/api/onboarding/properties-without-landlord`);
    if (ok && data.properties) setItems(data.properties);
  };

  const loadLandlords = async () => {
    const { ok, data } = await safeJsonFetch(`${API_BASE_URL}/api/landlords`);
    if (ok && Array.isArray(data)) setLandlords(data);
  };

  useEffect(() => {
    loadProperties();
    loadLandlords();
  }, []);

  if (collapsed || items.length === 0) return null;

  const linkLandlord = async (propertyId, landlordId) => {
    setLinking(propertyId);
    const { ok, data } = await safeJsonFetch(
      `${API_BASE_URL}/api/properties/${propertyId}/landlord`,
      { method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ landlord_id: landlordId }) }
    );
    setLinking(null);
    if (ok) {
      setOpenFor(null);
      setItems(prev => prev.filter(p => p.property_id !== propertyId));
    } else {
      window.alert('Could not link landlord: ' + (data.detail || 'Unknown error'));
    }
  };

  return (
    <div className="properties-no-landlord-banner"
         data-testid="properties-no-landlord-banner"
         style={{ background: '#fff7ed', border: '1px solid #fdba74',
                  borderRadius: 6, padding: '0.85rem 1rem',
                  margin: 0, color: '#9a3412' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
            Properties without a linked landlord
          </strong>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {items.slice(0, 10).map(p => (
              <li key={p.property_id}
                  data-testid={`property-no-landlord-${p.property_id}`}
                  style={{ padding: '0.4rem 0', borderTop: '1px solid #fed7aa',
                           display: 'flex', alignItems: 'center',
                           gap: '0.6rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', fontSize: '0.88rem', lineHeight: 1.4 }}>
                  <strong>{p.address}</strong> has no linked landlord.
                  {' '}Add one in <em>Settings &gt; Landlords</em> before
                  generating any Section 8 documents.
                </div>
                <div style={{ position: 'relative', flex: '0 0 auto' }}>
                  <button
                    type="button"
                    onClick={() => setOpenFor(openFor === p.property_id ? null : p.property_id)}
                    data-testid={`link-landlord-btn-${p.property_id}`}
                    style={{ background: '#ea580c', color: '#fff',
                             border: 'none', padding: '0.35rem 0.8rem',
                             borderRadius: 4, cursor: 'pointer',
                             fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {openFor === p.property_id ? 'Close' : 'Link Landlord'}
                  </button>
                  {openFor === p.property_id && (
                    <div data-testid={`link-landlord-dropdown-${p.property_id}`}
                         style={{ position: 'absolute',
                                  top: 'calc(100% + 4px)', right: 0,
                                  background: '#fff',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: 6,
                                  boxShadow: '0 6px 18px rgba(15,23,42,.12)',
                                  padding: '0.4rem 0', minWidth: 280,
                                  maxHeight: 260, overflowY: 'auto',
                                  zIndex: 20, color: '#0f172a' }}>
                      {landlords.length === 0 && (
                        <div style={{ padding: '0.6rem 0.8rem',
                                      fontSize: '0.85rem', color: '#64748b' }}>
                          No landlord profiles found. Open
                          {' '}<em>Settings &gt; Landlords</em> to create one.
                        </div>
                      )}
                      {landlords.map(ll => {
                        const label = (ll.full_name && ll.company_name)
                          ? `${ll.full_name} — ${ll.company_name}`
                          : (ll.full_name || ll.company_name || '—');
                        return (
                          <div key={ll.id || ll._id}
                               style={{ display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '0.4rem 0.8rem',
                                        borderBottom: '1px solid #f1f5f9',
                                        fontSize: '0.85rem' }}>
                            <span style={{ overflow: 'hidden',
                                           textOverflow: 'ellipsis',
                                           whiteSpace: 'nowrap',
                                           marginRight: '0.5rem' }}>
                              {label}
                            </span>
                            <button
                              type="button"
                              disabled={linking === p.property_id}
                              onClick={() => linkLandlord(p.property_id, ll.id || ll._id)}
                              data-testid={`link-landlord-select-${p.property_id}-${ll.id || ll._id}`}
                              style={{ background: '#0073EA', color: '#fff',
                                       border: 'none', padding: '0.28rem 0.7rem',
                                       borderRadius: 4, cursor: 'pointer',
                                       fontSize: '0.78rem' }}>
                              Select
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </li>
            ))}
            {items.length > 10 && (
              <li style={{ padding: '0.4rem 0',
                           fontSize: '0.85rem', color: '#7c2d12',
                           fontStyle: 'italic' }}>
                …and {items.length - 10} more.
              </li>
            )}
          </ul>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <button
            type="button"
            onClick={onGoToSettings}
            data-testid="properties-no-landlord-fix-now"
            style={{ background: '#fff', color: '#9a3412',
                     border: '1px solid #fdba74',
                     padding: '0.4rem 0.9rem',
                     borderRadius: 4, cursor: 'pointer',
                     fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            Open Landlords
          </button>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            data-testid="properties-no-landlord-dismiss"
            style={{ background: '#fff', color: '#9a3412',
                     border: '1px solid #fdba74',
                     padding: '0.4rem 0.9rem',
                     borderRadius: 4, cursor: 'pointer',
                     fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
